

    import Vue from 'vue';
    import Vue2TouchEvents from 'vue2-touch-events';

    import has from 'lodash/has';
    import { mapState, mapMutations, mapActions } from 'vuex';

    import CompanySlide from '@/components/Company/CompanySlide';
    import JobSave from '@/components/Job/JobSave';
    import CompanyLogo from '@/components/Company/CompanyLogo';
    import JobCardAvg from '@/components/Job/JobCard/JobCardAvg';
    import JobCardBadge from '@/components/Job/JobCard/JobCardBadge';
    import JobHide from '@/components/Job/JobCard/JobHide';
    import JobStatus from '@/components/Job/JobStatus';
    import JobDate from '@/components/Job/JobDate';
    import ScoreButton from '@/components/Scoring/ScoreButton';
    import JobCardCover from '@/components/Job/JobCard/JobCardCover';
    import scoreHelper from '@/utils/scoreHelper';
    import { slugify, imgResize } from '@/utils/helpers';
    import JobPay from '@/utils/JobPay.class';
    import { jobData } from '@/utils/jobData';
    import JobCardSimilarPay from '@/components/Job/JobCard/JobCardSimilarPay';
    import datalayer from '@/utils/datalayer';

    Vue.use(Vue2TouchEvents);

    export default {
        name: 'JobCardBasic',

        components: {
            CompanySlide,
            JobSave,
            JobHide,
            CompanyLogo,
            JobCardAvg,
            JobCardBadge,
            JobDate,
            ScoreButton,
            JobCardCover,
            JobStatus,
            JobCardSimilarPay,
        },

        props: {
            target: {
                type: String,
                default: '_self',
            },

            job: {
                type: Object,
                default: () => { return {}; },
            },

            noHover: {
                type: Boolean,
                default: false,
            },

            jobIndex: {
                type: Number,
                default: 0,
            },

            rowLabel: {
                type: String,
                default: '',
            },

            rowType: {
                type: String,
                default: '',
            },

            rowPosition: {
                type: Number,
                default: 0,
            },
        },

        data() {
            return {
                jobMutable: {},
                isMenuOpen: false,
                shouldImgSlideShow: false,
                showPromptModal: false,
                onBoardingPrompt: {
                    component_name: 'OnBoardingPrompt',
                    layout: 'lg',
                    code: 'ON_BOARDING_PROMPT',
                    page_referral_element: 'score_button',
                },
            };
        },

        computed: {
            ...mapState({
                $_me: state => state.me.user,
                $_scoringData: state => state.me.scoringData,
                $_scoringMatrixData: state => state.me.scoringMatrixData,
                $_appInfo: state => state.appInfo,
            }),

            classList() {
                return [
                    this.getPrivateListingClass,
                    this.getNoHoverClass,
                    this.getIsPayEstimatedClass,
                    this.getIsDetailedView,
                ];
            },

            getPrivateListingClass() {
                return this.isPrivateListing ? 'is-private' : '';
            },

            getNoHoverClass() {
                return this.noHover ? 'no-hover' : '';
            },

            getIsPayEstimatedClass() {
                return this.isPayEstimated ? 'is-pay-estimated' : '';
            },

            getIsDetailedView() {
                return this.showDetails ? 'is-show-detailed' : '';
            },

            showImgSlide() {
                return this.shouldImgSlideShow && this.brandSlideImages.length && this.$_appInfo.activeTile === this._uid;
            },

            showPay() {
                if (!this.jobMutable.pay_type) {
                    return false;
                }

                if (this.jobMutable.pay_min <= 0 && this.jobMutable.pay_max <= 0) {
                    return false;
                }

                return true;
            },

            companySquareLogoUrl() {
                return this.jobMutable.company_square_logo_url || '';
            },

            pay() {
                return new JobPay(
                    this.jobMutable.pay_min,
                    this.jobMutable.pay_max,
                    this.jobMutable.pay_type,
                    false,
                    this.isPayEstimated,
                    this.jobMutable.job_type_label,
                    this.jobMutable.contract_type_label,
                );
            },

            payMinMaxLabel() {
                return this.pay ? this.pay.payMinMaxLabel : '';
            },

            payMinMaxShortLabel() {
                return this.pay ? this.pay.payMinMaxShortLabel : '';
            },

            cta() {
                return {
                    name: 'jobview-slug-id',
                    params: {
                        slug: slugify(this.jobMutable.job_title),
                        id: this.jobMutable.uuid,
                    },
                };
            },

            ctaLink() {
                if (this.job.apply_type_id === 8) {
                    return `${this.$siteUrl}job-redirect/${this.job.uuid}`;
                }
                if (this.rowLabel) {
                    return `${this.$router.resolve(this.cta).href}?page_referral_element=${this.rowLabel}`;
                }
                return this.$router.resolve(this.cta).href;
            },

            scoring() {
                return scoreHelper.calculateScoreFrontEnd(this.jobMutable, this.$_scoringData, this.$_scoringMatrixData);
            },

            brandMedias() {
                return has(this.jobMutable, 'brand_details.medias') && this.jobMutable.brand_details.medias.length ? this.jobMutable.brand_details.medias : [];
            },

            brandImages() {
                return has(this.jobMutable, 'brand_details.medias') ? this.jobMutable.brand_details.medias.filter(o => o.mediaType.name === 'Company Image') : [];
            },

            brandCover() {
                return this.brandImages.length ? imgResize(this.brandImages[0].url, 300, 300) : '';
            },

            brandSlideImages() {
                return this.brandImages.length ? this.brandImages.slice(1) : [];
            },

            brandPrimaryColor() {
                return has(this.jobMutable, 'brand_details.primary_color') ? this.jobMutable.brand_details.primary_color : '';
            },

            showBrandEEOBadge() {
                return has(this.jobMutable, 'brand_details.equal_opportunity_tags') && this.jobMutable.brand_details.equal_opportunity_tags.length;
            },

            brandEEOBadge() {
                return this.showBrandEEOBadge ? require('@/assets/img/EEO.svg') : null;
            },

            isPrivateListing() {
                return this.jobMutable.brand_is_hidden;
            },

            isPayEstimated() {
                return !!this.jobMutable.pay_is_estimated;
            },

            payDescription() {
                return this.jobMutable.pay_description || '';
            },

            isPayAboveAvg() {
                return !!this.jobMutable.pay_is_above_avg;
            },

            companyNameLabel() {
                return this.isPrivateListing ? 'Private Listing' : this.jobMutable.company_name;
            },

            primaryCompanyNameLabel() {
                return this.isPrivateListing ? '' : this.job.primary_brand_name;
            },

            companyLink() {
                if (this.jobMutable.brand_slug && !this.jobMutable.brand_is_hidden) {
                    return `/jobs/br_${this.jobMutable.brand_slug}`;
                }
                return '/jobs';
            },

            primaryCompanyLink() {
                if (this.jobMutable.primary_brand_slug) {
                    return `/jobs/br_${this.jobMutable.primary_brand_slug}`;
                }
                return '';
            },

            jobData() {
                return {
                    ...jobData(this.jobMutable),
                    seeker_match_score: this.scoring,
                    seeker_data: this.$_scoringData,
                };
            },
        },

        created() {
            this.jobMutable = JSON.parse(JSON.stringify(this.job));
        },

        methods: {
            ...mapMutations('jobModal', {
                $_STORE_JOB_UUID: 'STORE_JOB_UUID',
                $_STORE_VERTICAL_SCROLL_POSITION: 'STORE_VERTICAL_SCROLL_POSITION',
                $_STORE_SECTION_TITLE: 'STORE_SECTION_TITLE',
            }),

            ...mapActions('eventTracking', {
                $_pushEvent: 'pushEvent',
                $_pushSeekerJobEvent: 'pushSeekerJobEvent',
            }),

            getLocationLink() {
                const routePath = this.$route.path;
                if (routePath.indexOf('-jobs') > 0 && routePath.indexOf('/in-') <= 0) {
                    return `${routePath.split('-jobs')[0]}-jobs/in-${this.jobMutable.location_level_2_slug || this.jobMutable.location_slug}`;
                } else {
                    return `/jobs/in-${this.jobMutable.location_level_2_slug || this.jobMutable.location_slug}`;
                }
            },
            getRegionalLink() {
                const routePath = this.$route.path;
                if (routePath.indexOf('-jobs') > 0 && routePath.indexOf('/in-') <= 0) {
                    return `${routePath.split('-jobs')[0]}-jobs/in-${this.jobMutable.location_level_3_slug || this.jobMutable.location_level_2_slug}`;
                } else {
                    return `/jobs/in-${this.jobMutable.location_level_3_slug || this.jobMutable.location_level_2_slug}`;
                }
            },
            getLocationLabel() {
                let url = this.jobMutable.location_level_2_label || this.jobMutable.location_label || '';
                if (url === this.jobMutable.location_level_3_label) {
                    url = '';
                }
                return url;
            },

            getCategoryLink() {
                const routePath = this.$route.path;
                let categoryLink = `/jobs-in-${this.jobMutable.category_slug}`;
                if (this.jobMutable.location_level_3_slug && !routePath.includes('/in-')) {
                    const locationLink = `in-${this.jobMutable.location_level_3_slug}`;
                    categoryLink = `${categoryLink}/${locationLink}`;
                }
                return categoryLink;
            },

            getOccupationLink() {
                const routePath = this.$route.path;
                let occupationLink = '/jobs';
                if (this.jobMutable.location_level_3_slug && !routePath.includes('/in-')) {
                    const locationLink = `in-${this.jobMutable.location_level_3_slug}`;
                    occupationLink = `${occupationLink}/${locationLink}`;
                }
                occupationLink = `${occupationLink}/occ_${this.jobMutable.occupation_slug}`;
                return occupationLink;
            },

            getEEOTags() {
                if (this.jobMutable.brand_is_hidden) {
                    return [];
                }
                return this.jobMutable.brand_details?.equal_opportunity_tags;
            },

            goToJob(modal = false, rowLabel = '', target = '_blank') {
                this.jobClickEvent();
                if (this.rowType === 'SIMILAR_JOBS') {
                    modal = false;
                }
                if (this.job.apply_type_id === 8) {
                    modal = false;
                    target = '_blank';
                }
                modal ? this.openJobModal(rowLabel) : this.redirectJob(target);
            },

            openJobModal(rowLabel = '') {
                this.$_STORE_JOB_UUID(this.jobMutable.uuid);
                this.$_STORE_VERTICAL_SCROLL_POSITION(window.scrollY);
                this.$_STORE_SECTION_TITLE(rowLabel);
            },

            redirectJob(target = '_blank') {
                window.open(this.ctaLink, target);
            },

            visibilityChanged(isVisible) {
                if (isVisible) {
                    const data = {
                        event: 'job_impression',
                        site_position: this.rowType,
                        page_referral_element: this.rowLabel,
                        site_position_label: this.rowLabel,
                        job_position: this.jobIndex + 1,
                        row_position: this.rowPosition + 1,
                        ...this.jobData,
                    };
                    this.$_pushEvent(data);
                    datalayer(data);
                }
            },

            jobClickEvent() {
                const data = {
                    event: 'job_click',
                    site_position: this.rowType,
                    page_referral_element: this.rowLabel,
                    site_position_label: this.rowLabel,
                    job_position: this.jobIndex + 1,
                    row_position: this.rowPosition + 1,
                    ...this.jobData,
                };
                this.$_pushEvent(data);
                this.$_pushSeekerJobEvent(data);
                datalayer(data);
            },

            saveJob() {
                this.spinner = true;
                this.$_storeSavedJob({ job_uuid: this.jobMutable.uuid }).then((response) => {
                    this.$_PUT_JOB_EVENT({ job: this.jobMutable.uuid, status: 'is_saved' });
                    this.$_INCREASE_DISCOVERY_NAV_COUNT('saved_jobs');
                }).finally(() => {
                    this.spinner = false;
                });
            },

            openMenu() {
                this.isMenuOpen = true;
            },

            closeMenu() {
                this.isMenuOpen = false;
            },

            onMouseOver() {
                this.shouldImgSlideShow = true;
                this.$store.commit('appInfo/STORE_ACTIVE_TILE', this._uid);
            },

            onMouseOut() {
                this.shouldImgSlideShow = false;
                this.$store.commit('appInfo/STORE_ACTIVE_TILE', '');
            },

            openPromptModal() {
                this.showPromptModal = true;
            },

            closePromptModal() {
                this.showPromptModal = false;
            },
        },
    };
